import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Users,
  GraduationCap,
  UserCheck,
  FileText,
  Trophy,
  User,
  LogOut,
  ChevronRight,
  Calendar,
  Grid3x3,
  BookOpen,
  Target,
  DollarSign,
  Activity,
  Clock,
  Monitor,
  Award,
  Wallet,
  Sparkles,
  TrendingUp,
  BarChart3,
  Building2,
  Search,
  X,
  PanelLeftClose,
  ShieldCheck,
  ClipboardList,
  Scale,
  Compass,
  Rocket,
  Brain,
  HelpCircle
} from "lucide-react";

import BrandLogo from "../Assets/HE.AI-Logo.png";
import { getMenuItemsForRole } from "../utils/permissions";

const SCROLL_KEY = "admin_sidebar_scroll";

// key -> icon element, one entry per MENU_ITEMS key
const ICON_MAP = {
  home: <Home />,
  analytics: <BarChart3 />,
  management_dashboard: <Users />,
  hod_dashboard: <GraduationCap />,
  cocurricular_dashboard: <Activity />,

  onboarding: <Users />,
  teachers: <UserCheck />,
  students: <GraduationCap />,
  parents: <Users />,
  hods: <Award />,
  finance_staff: <Wallet />,
  cocurricular_staff: <Sparkles />,
  tech_leaders: <Monitor />,
  ClassManagement: <GraduationCap />,
  departments: <Building2 />,

  holistic_auditing: <ShieldCheck />,
  school_compliances: <ShieldCheck />,
  CBSECirculars: <FileText />,
  sops: <ClipboardList />,
  affiliation_laws: <Scale />,

  sdp: <Compass />,
  sdp_plans: <BookOpen />,
  sdp_dashboard: <BarChart3 />,
  sdp_quick_start: <Sparkles />,
  sdp_identity: <FileText />,
  sdp_school_info: <FileText />,
  sdp_expenditure: <DollarSign />,
  sdp_teams: <Users />,
  sdp_market: <TrendingUp />,
  sdp_diagnosis: <Activity />,
  sdp_swot: <Grid3x3 />,
  sdp_goals: <Target />,
  sdp_actions: <BookOpen />,
  sdp_monitoring: <Monitor />,
  sdp_reports: <FileText />,
  sdp_complete: <Award />,

  acpp: <Calendar />,
  acpp_plans: <BookOpen />,
  acpp_m1: <FileText />,
  acpp_m2: <FileText />,
  acpp_m3: <GraduationCap />,
  acpp_m4: <BookOpen />,
  acpp_m5: <UserCheck />,
  acpp_m6: <FileText />,
  acpp_m7: <Target />,
  acpp_m8: <TrendingUp />,
  acpp_m9: <Users />,
  acpp_m10: <Calendar />,
  acpp_m11: <Grid3x3 />,
  acpp_m12: <BarChart3 />,
  acpp_m13: <BookOpen />,

  CalendarManagement: <Calendar />,
  EisenhowerMatrix: <Grid3x3 />,

  teacher_tracker: <TrendingUp />,
  growth_overview: <TrendingUp />,
  LessonPlanManagement: <BookOpen />,
  AssessmentManagement: <FileText />,
  CompetencyManagement: <Target />,
  teacher_appraisal: <Award />,
  student_reporting: <FileText />,
  academic_transcript_reports: <FileText />,
  HPCManagement: <FileText />,
  future_ready_reports: <Rocket />,
  leaderboard: <Trophy />,
  student_reports_centre: <FileText />,
  growth_achievement_reports: <TrendingUp />,

  finance_dashboard: <DollarSign />,
  admission_booster: <Users />,

  leaders_self_growth: <Sparkles />,
  profile: <User />,
  self_awareness: <Brain />,
  time_management: <Clock />,

  logout: <LogOut />
};

const Icon = ({ item, className = "hesb-ic" }) => (
  <span className={className}>{ICON_MAP[item.key] || <HelpCircle />}</span>
);

function Highlight({ text, query }) {
  if (!query) return text;
  const i = text.toLowerCase().indexOf(query);
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark>{text.slice(i, i + query.length)}</mark>
      {text.slice(i + query.length)}
    </>
  );
}

/** Turn the flat, permission-filtered MENU_ITEMS list into sections of
 *  { item, children? } — group headers (sdp/acpp) swallow the items that
 *  follow them sharing the same `group`. Logout is dropped; it lives in
 *  the footer instead. */
function buildSections(menuItems) {
  const sections = [];
  let current = { title: null, entries: [] };
  sections.push(current);

  for (let i = 0; i < menuItems.length; i++) {
    const item = menuItems[i];
    if (item.key === "logout") continue;

    if (item.isSectionHeader) {
      current = { title: item.text, entries: [] };
      sections.push(current);
      continue;
    }
    if (item.group && !item.isGroupHeader) continue; // consumed by its header below

    if (item.isGroupHeader) {
      const children = menuItems.filter((m) => m.group === item.group && !m.isGroupHeader);
      current.entries.push({ item, children });
    } else {
      current.entries.push({ item, children: null });
    }
  }

  return sections.filter((s) => s.entries.length > 0);
}

const getStoredAcademicYear = (storageKey) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey) || "{}");
    return String(parsed?.academicYear || "").trim();
  } catch (_) {
    return "";
  }
};

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(() => ({
    sdp: window.location.pathname.startsWith("/sdp/"),
    annual_planning: window.location.pathname === "/ACPP" || window.location.pathname.startsWith("/acpp/")
  }));
  // groups whose route scheme isn't fully captured by an exact child-path match
  // (sdp mounts its own nested router, so most of its pages aren't in this list)
  const ROUTE_PREFIX_GROUPS = { sdp: "/sdp/" };
  const [query, setQuery] = useState("");
  const [rail, setRail] = useState(null); // { id, top, left }

  const navRef = useRef(null);
  const searchRef = useRef(null);
  const q = query.trim().toLowerCase();

  const userRole = localStorage.getItem("user_role") || "admin";
  const menuItems = useMemo(() => getMenuItemsForRole(userRole), [userRole]);
  const sections = useMemo(() => buildSections(menuItems), [menuItems]);

  // auto-expand whichever group contains the current route — without
  // clobbering a group the user closed by hand
  useEffect(() => {
    setExpandedGroups((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const section of sections) {
        for (const { item, children } of section.entries) {
          if (!item.isGroupHeader || next[item.group]) continue;
          const prefix = ROUTE_PREFIX_GROUPS[item.group];
          const onRoute =
            (prefix && location.pathname.startsWith(prefix)) ||
            (item.group === "annual_planning" && location.pathname === "/ACPP") ||
            (children ?? []).some((k) => k.path && location.pathname === k.path.split("#")[0]);
          if (onRoute) {
            next[item.group] = true;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, sections]);

  // restore nav scroll position across the route-driven remounts of this component
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    el.scrollTop = parseInt(sessionStorage.getItem(SCROLL_KEY) || "0", 10);
  }, []);
  const saveScroll = () => {
    if (navRef.current) sessionStorage.setItem(SCROLL_KEY, String(navRef.current.scrollTop));
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !collapsed && document.activeElement !== searchRef.current) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [collapsed]);

  useEffect(() => {
    if (window.matchMedia("(max-width:720px)").matches) setCollapsed(true);
  }, []);

  const isActive = (path) => {
    if (!path) return false;
    if (path.includes("#")) {
      const [pathname, hash] = path.split("#");
      return location.pathname === pathname && location.hash === `#${hash}`;
    }
    return location.pathname === path;
  };

  const getSdpAcademicYear = () => getStoredAcademicYear("school_portal_settings");
  const getAcppAcademicYear = () => getStoredAcademicYear("acpp_portal_settings") || getSdpAcademicYear();
  const getSqaafAcademicYear = () => {
    const match = location.pathname.match(/^\/SQAAF\/([^/?#]+)/i);
    if (match?.[1]) {
      try {
        return decodeURIComponent(match[1]);
      } catch (_) {
        return match[1];
      }
    }
    return getSdpAcademicYear();
  };
  const yearFor = (key) => {
    if (key === "sdp") return getSdpAcademicYear();
    if (key === "acpp") return getAcppAcademicYear();
    if (key === "SQAAF") return getSqaafAcademicYear();
    return "";
  };

  function go(item) {
    if (item.text === "Logout") {
      localStorage.removeItem("accessToken");
      navigate("/");
      return;
    }
    navigate(item.path);
  }

  function toggleGroup(item) {
    if (collapsed) setCollapsed(false);
    setExpandedGroups((prev) => ({ ...prev, [item.group]: !prev[item.group] }));
    go(item);
  }

  function collapseSidebar(next) {
    setCollapsed(next);
    setRail(null);
    if (next) setQuery("");
  }

  function enterRail(e, item, hasChildren) {
    if (!collapsed) return;
    const r = e.currentTarget.getBoundingClientRect();
    setRail({
      id: item.key,
      kind: hasChildren ? "flyout" : "tip",
      left: r.right + 10,
      top: hasChildren ? Math.max(12, r.top - 8) : r.top + r.height / 2 - 20
    });
  }

  /* filter: which entries/children survive the search query */
  const view = useMemo(() => {
    return sections
      .map((section) => {
        const entries = section.entries.map(({ item, children }) => {
          const parentHit = !q || item.text.toLowerCase().includes(q);
          const kids = children ?? [];
          const kidHits = q ? kids.filter((k) => k.text.toLowerCase().includes(q)) : kids;
          return {
            item,
            children,
            visible: parentHit || kidHits.length > 0,
            parentHit,
            shownChildren: !q || parentHit ? kids : kidHits,
            hitChildKeys: new Set(kidHits.map((k) => k.key))
          };
        });
        return { ...section, entries, visible: entries.some((e) => e.visible) };
      })
      .filter((s) => s.visible);
  }, [sections, q]);

  const noResults = view.length === 0;

  return (
    <>
      <style>{STYLES}</style>

      <aside className={`hesb${collapsed ? " hesb-collapsed" : ""}`}>
        <div className="hesb-brand">
          <button onClick={() => navigate("/HomePage")} aria-label="Home" title="Home" className="hesb-brand-btn">
            <img src={BrandLogo} alt="HE.AI" className="hesb-brand-logo" />
          </button>
        </div>

        <div className={`hesb-search${q ? " hesb-filled" : ""}`}>
          <Search className="hesb-search-ic" size={18} />
          <input
            ref={searchRef}
            type="search"
            value={query}
            placeholder="Find a section"
            aria-label="Find a section"
            autoComplete="off"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setQuery("");
                e.currentTarget.blur();
              }
            }}
          />
          <button
            className="hesb-search-clear"
            aria-label="Clear search"
            onClick={() => {
              setQuery("");
              searchRef.current?.focus();
            }}
          >
            <X size={14} />
          </button>
        </div>

        <nav
          ref={navRef}
          className="hesb-nav"
          aria-label="Main"
          onScroll={saveScroll}
          onMouseDown={saveScroll}
        >
          {view.map((section, si) => (
            <div className="hesb-section" key={section.title ?? `s-${si}`}>
              {section.title && <div className="hesb-section-title">{section.title}</div>}

              {section.entries.map(({ item, children, visible, parentHit, shownChildren, hitChildKeys }) => {
                if (!visible) return null;
                const hasKids = !!item.isGroupHeader;
                const open = hasKids && (expandedGroups[item.group] || (!!q && shownChildren.length > 0));
                const groupActive =
                  hasKids &&
                  ((ROUTE_PREFIX_GROUPS[item.group] && location.pathname.startsWith(ROUTE_PREFIX_GROUPS[item.group])) ||
                    (item.group === "annual_planning" && (location.pathname === "/ACPP" || location.pathname.startsWith("/acpp/"))));
                const childClaimsPath =
                  hasKids && (children ?? []).some((k) => isActive(k.path));
                const isThisActive = hasKids ? isActive(item.path) && !childClaimsPath : isActive(item.path);
                const hasSelection = hasKids && childClaimsPath;
                const railOpen = rail?.id === item.key;
                const year = yearFor(item.key);

                return (
                  <div
                    key={item.key}
                    className={
                      "hesb-group" +
                      (hasKids ? " hesb-has-sub" : "") +
                      (open ? " hesb-open" : "") +
                      (hasSelection || groupActive ? " hesb-has-selection" : "") +
                      (railOpen ? " hesb-rail" : "")
                    }
                    onPointerEnter={(e) => enterRail(e, item, hasKids)}
                    onPointerLeave={() => setRail(null)}
                  >
                    <button
                      className={`hesb-item${isThisActive ? " hesb-active" : ""}`}
                      aria-expanded={hasKids ? open : undefined}
                      aria-label={item.text}
                      onClick={() => (hasKids ? toggleGroup(item) : go(item))}
                    >
                      <Icon item={item} />
                      <span className="hesb-label">
                        <Highlight text={item.text} query={parentHit ? q : ""} />
                        {year && <span className="hesb-year">{year}</span>}
                      </span>
                      {hasKids && <ChevronRight className="hesb-chev" size={16} />}
                    </button>

                    {!hasKids && collapsed && railOpen && (
                      <span className="hesb-tip" style={{ left: rail.left, top: rail.top }}>
                        {item.text}
                      </span>
                    )}

                    {hasKids && (
                      <div
                        className="hesb-sub"
                        style={collapsed && railOpen ? { left: rail.left, top: rail.top } : undefined}
                      >
                        <div className="hesb-sub-wrap">
                          <div className="hesb-sub-inner">
                            <div className="hesb-flyout-title">{item.text}</div>
                            {shownChildren.map((kid) => (
                              <button
                                key={kid.key}
                                className={`hesb-subitem${isActive(kid.path) ? " hesb-active" : ""}`}
                                onClick={() => go(kid)}
                              >
                                <Icon item={kid} className="hesb-ic hesb-ic-sm" />
                                <span className="hesb-sub-label">
                                  <Highlight text={kid.text} query={hitChildKeys.has(kid.key) ? q : ""} />
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {noResults && <p className="hesb-empty">Nothing matches that. Try a shorter word.</p>}
        </nav>

        <div className="hesb-foot">
          <div
            className={`hesb-has-tip${rail?.id === "__logout" ? " hesb-rail" : ""}`}
            onPointerEnter={(e) => enterRail(e, { key: "__logout" }, false)}
            onPointerLeave={() => setRail(null)}
          >
            <button
              className="hesb-item hesb-logout"
              aria-label="Log Out"
              onClick={() => go({ text: "Logout" })}
            >
              <span className="hesb-ic">
                <LogOut />
              </span>
              <span className="hesb-label">Log Out</span>
            </button>
            {collapsed && rail?.id === "__logout" && (
              <span className="hesb-tip" style={{ left: rail.left, top: rail.top }}>
                Log Out
              </span>
            )}
          </div>

          <button
            className="hesb-collapse-btn"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!collapsed}
            onClick={() => collapseSidebar(!collapsed)}
          >
            <PanelLeftClose className="hesb-ic-collapse" size={17} />
            <span className="hesb-label">Collapse</span>
          </button>
        </div>
      </aside>
    </>
  );
}

/* ============================================================
   STYLES — scoped under .hesb so nothing leaks into the rest of
   the app (Tailwind's own `.group` utility included). Flat,
   standard admin-panel look: no floating cards, no hover lift,
   no dim/blur-on-hover of neighboring items.
   ============================================================ */
const STYLES = `
.hesb, .hesb *{box-sizing:border-box;}
.hesb{
  --sb-w: 264px;
  --sb-w-collapsed: 76px;

  --surface: #FFFFFF;
  --sunken: #F5F7FA;
  --hover: #F0F3F7;

  --line: #E4E8EE;
  --line-soft: #ECEFF3;

  --text: #1F2937;
  --text-soft: #4B5566;
  --text-mute: #8A94A3;

  --accent: #2563EB;
  --accent-deep: #1D4ED8;
  --accent-tint: #EFF4FE;

  --brand: #F57F20;

  --ease: cubic-bezier(.2,.6,.3,1);

  font-family:'Poppins',system-ui,sans-serif;
  width:var(--sb-w);
  height:100vh;
  display:flex;flex-direction:column;position:relative;
  background:var(--surface);
  border-right:1px solid var(--line);
  transition:width .2s var(--ease);
  color:var(--text);
}
.hesb.hesb-collapsed{width:var(--sb-w-collapsed);}

.hesb-brand{display:flex;align-items:center;justify-content:center;padding:18px 12px;flex:0 0 auto;border-bottom:1px solid var(--line);}
.hesb-brand-btn{border:0;background:transparent;cursor:pointer;padding:0;display:block;}
.hesb-brand-logo{height:54px;width:auto;object-fit:contain;display:block;margin:0 auto;}
.hesb-collapsed .hesb-brand-logo{height:40px;}

.hesb-search{position:relative;margin:12px 12px 4px;}
.hesb-collapsed .hesb-search{display:none;}
.hesb-search-ic{position:absolute;left:12px;top:50%;margin-top:-9px;color:var(--text-mute);pointer-events:none;}
.hesb-search input{
  width:100%;padding:9px 32px 9px 36px;
  border:1px solid var(--line);border-radius:8px;
  background:var(--sunken);color:var(--text);
  font-family:inherit;font-size:13px;
  transition:border-color .15s var(--ease), background .15s var(--ease);
}
.hesb-search input::placeholder{color:var(--text-mute);}
.hesb-search input:focus{outline:none;border-color:var(--accent);background:#FFF;}
.hesb-search-clear{
  position:absolute;right:6px;top:50%;margin-top:-12px;width:24px;height:24px;
  display:none;align-items:center;justify-content:center;
  border:0;border-radius:6px;background:transparent;color:var(--text-mute);cursor:pointer;
}
.hesb-search-clear:hover{background:var(--hover);color:var(--text);}
.hesb-search.hesb-filled .hesb-search-clear{display:flex;}

.hesb-nav{
  flex:1;min-height:0;overflow-y:auto;overflow-x:visible;
  padding:6px 8px 10px;
  scrollbar-width:thin;scrollbar-color:var(--line) transparent;
}
.hesb-nav::-webkit-scrollbar{width:6px;}
.hesb-nav::-webkit-scrollbar-thumb{background:var(--line);border-radius:99px;}

.hesb-section-title{
  font-size:11px;font-weight:600;letter-spacing:.4px;color:var(--text-mute);
  padding:14px 10px 6px;text-transform:uppercase;
}
.hesb-section:first-child .hesb-section-title{padding-top:8px;}
.hesb-collapsed .hesb-section-title{
  font-size:0;padding:8px 0;height:1px;margin:8px 12px;background:var(--line-soft);
}
.hesb-group{position:relative;}
.hesb-empty{padding:16px 12px;color:var(--text-mute);font-size:12.5px;line-height:1.6;}

.hesb-item{
  position:relative;display:flex;align-items:center;gap:10px;width:100%;
  padding:9px 10px;margin:1px 0;border:0;border-radius:8px;
  background:transparent;color:var(--text-soft);
  font-family:inherit;font-size:13.5px;font-weight:500;line-height:1.3;text-align:left;
  cursor:pointer;
  transition:background .15s var(--ease), color .15s var(--ease);
}
.hesb-item:focus-visible{outline:2px solid var(--accent);outline-offset:1px;}
.hesb-item:hover{color:var(--text);background:var(--hover);}
.hesb-item:active{background:var(--line-soft);}

.hesb-ic{
  display:flex;align-items:center;justify-content:center;
  width:22px;height:22px;flex:0 0 22px;
  color:var(--text-mute);
  transition:color .15s var(--ease);
}
.hesb-ic svg{width:18px;height:18px;}
.hesb-ic-sm{width:18px;height:18px;flex:0 0 18px;}
.hesb-ic-sm svg{width:15px;height:15px;}
.hesb-label{flex:1;min-width:0;overflow-wrap:anywhere;}
.hesb-year{margin-left:6px;font-size:10px;font-weight:500;color:var(--accent-deep);background:var(--accent-tint);padding:1px 6px;border-radius:99px;}
.hesb-chev{flex:0 0 16px;color:var(--text-mute);transition:transform .2s var(--ease);}

.hesb-group.hesb-open > .hesb-item .hesb-chev{transform:rotate(90deg);}
.hesb-group.hesb-open > .hesb-item{color:var(--text);background:var(--hover);}

.hesb-item.hesb-active{
  color:var(--accent-deep);font-weight:600;
  background:var(--accent-tint);
}
.hesb-item.hesb-active .hesb-ic{color:var(--accent);}
.hesb-item.hesb-active::before{
  content:"";position:absolute;left:-8px;top:50%;width:3px;height:18px;margin-top:-9px;
  border-radius:0 3px 3px 0;background:var(--accent);
}
.hesb-group.hesb-has-selection > .hesb-item::before{
  content:"";position:absolute;left:-8px;top:50%;width:3px;height:18px;margin-top:-9px;
  border-radius:0 3px 3px 0;background:var(--accent);opacity:.45;
}

.hesb-sub{display:grid;grid-template-rows:0fr;transition:grid-template-rows .22s var(--ease);}
.hesb-sub-wrap{overflow:hidden;min-height:0;}
.hesb-group.hesb-open .hesb-sub{grid-template-rows:1fr;}
.hesb-sub-inner{
  margin:2px 0 6px 20px;padding:2px 0 2px 12px;
  border-left:1px solid var(--line);
  display:flex;flex-direction:column;gap:1px;
}
.hesb-flyout-title{display:none;}
.hesb-subitem{
  position:relative;display:flex;align-items:center;gap:8px;width:100%;min-height:34px;
  padding:7px 8px;border:0;border-radius:7px;background:transparent;
  color:var(--text-soft);font-family:inherit;font-size:13px;font-weight:400;line-height:1.4;
  text-align:left;cursor:pointer;
  transition:background .15s var(--ease), color .15s var(--ease);
}
.hesb-subitem:hover{color:var(--text);background:var(--hover);}
.hesb-subitem:active{background:var(--line-soft);}
.hesb-subitem.hesb-active{
  color:var(--accent-deep);font-weight:600;
  background:var(--accent-tint);
}
.hesb mark{background:#FDE9C8;color:inherit;border-radius:3px;padding:0 1px;}

.hesb-foot{
  padding:8px;display:flex;flex-direction:column;gap:4px;flex:0 0 auto;
  border-top:1px solid var(--line);
}
.hesb-logout{color:var(--text-soft);}
.hesb-logout .hesb-ic{color:var(--text-mute);}
.hesb-logout:hover{color:#B4470F;background:#FDF0E6;}
.hesb-logout:hover .hesb-ic{color:#B4470F;}

.hesb-collapse-btn{
  display:flex;align-items:center;justify-content:center;gap:8px;width:100%;
  padding:9px;border:0;border-radius:8px;
  background:transparent;
  color:var(--text-mute);font-family:inherit;font-size:12.5px;font-weight:500;cursor:pointer;
  transition:background .15s var(--ease), color .15s var(--ease);
}
.hesb-collapse-btn:hover{color:var(--text);background:var(--hover);}
.hesb-ic-collapse{transition:transform .2s var(--ease);}
.hesb-collapsed .hesb-ic-collapse{transform:rotate(180deg);}
.hesb-collapsed .hesb-collapse-btn .hesb-label{display:none;}

/* collapsed rail */
.hesb-collapsed .hesb-item{justify-content:center;padding:10px 0;gap:0;}
.hesb-collapsed .hesb-item .hesb-label,
.hesb-collapsed .hesb-item .hesb-chev{display:none;}
.hesb-collapsed .hesb-item.hesb-active::before,
.hesb-collapsed .hesb-group.hesb-has-selection > .hesb-item::before{display:none;}

.hesb-collapsed .hesb-sub{display:none;}
.hesb-collapsed .hesb-group.hesb-rail > .hesb-sub{
  display:block;position:fixed;z-index:90;
  width:236px;overflow:visible;
  border:1px solid var(--line);border-radius:10px;background:var(--surface);
  box-shadow:0 8px 24px rgba(15,23,42,.12);padding:6px;
}
.hesb-collapsed .hesb-group.hesb-rail .hesb-sub-wrap{overflow:visible;}
.hesb-collapsed .hesb-group.hesb-rail .hesb-sub-inner{
  margin:0;padding:0;border-left:0;
}
.hesb-collapsed .hesb-group.hesb-rail .hesb-flyout-title{
  display:block;padding:8px 10px 6px;font-size:11.5px;font-weight:600;color:var(--text-mute);
}

.hesb-tip{
  position:fixed;z-index:100;
  padding:8px 12px;border:1px solid var(--line);border-radius:8px;
  background:var(--surface);color:var(--text);font-size:12.5px;white-space:nowrap;
  box-shadow:0 8px 20px rgba(15,23,42,.14);
  pointer-events:none;
}
.hesb-has-tip{position:relative;}

@media (prefers-reduced-motion:reduce){
  .hesb *{transition-duration:.01ms !important;}
}
`;
